import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { LoadingPageService } from '@umss/shared/ui';

export const enum BusinessTabDetailEntity {
  Facility = 'Facility',
  Corporation = 'Corporation',
  Contractor = 'Contractor',
  Invoice = 'Invoice'
}

@Injectable({ providedIn: 'root' })
export class BusinessTabTitleService {
  private readonly appName = 'HURO';

  private readonly title = inject(Title);
  private readonly loadingPageService = inject(LoadingPageService);
  constructor() {
    // Listen to loading state changes to update the title accordingly
    this.loadingPageService.navigationEnd$.subscribe((url) => {
      this.setForUrl(url);
    });
    this.setForUrl(window.location.pathname);
  }

  setForUrl(rawUrl: string): void {
    const url = this.normalizeUrl(rawUrl);
    this.setDocumentTitle(this.resolveFallbackTitle(url));
  }

  setDetailTitle(
    entity: BusinessTabDetailEntity | `${BusinessTabDetailEntity}`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any
  ): void {
    switch (entity) {
      case 'Facility':
        return this.setDocumentTitle(
          `Facility · ${payload?.name || 'Details'}`
        );
      case 'Invoice':
        return this.setDocumentTitle(
          `Invoice · ${payload?.invoiceNumber || 'Details'}`
        );
      case 'Contractor':
        return this.setDocumentTitle(
          `Professional · ${payload?.firstName + payload?.lastName || 'Details'}`
        );
      case 'Corporation':
        return this.setDocumentTitle(
          `Corporation · ${payload?.name || 'Details'}`
        );
      default:
        return this.setDocumentTitle(this.appName);
    }
  }

  private normalizeUrl(rawUrl: string): string {
    return rawUrl.split('?')[0].toLowerCase();
  }

  private resolveFallbackTitle(url: string): string {
    if (url.includes('/dashboard')) return 'Dashboard';
    // Before the bare `/payments` test, which its URL also matches.
    if (url.includes('/payments/subscriptions')) return 'Subscription Payments';
    if (url.includes('/payments')) return 'Shift Payments';
    if (url.includes('/facilities')) return 'Facilities & Corporations';
    if (url.includes('/professionals')) return 'Professionals';
    if (url.includes('/admins')) return 'Administrators';
    if (url.includes('/communications')) return 'Communications';
    if (url.includes('/auth/login')) return 'Business portal · Sign in';
    if (url.includes('/auth/register')) return 'Business portal · Sign up';
    if (url.includes('/auth/terms-of-service'))
      return 'HURO · Terms of Service';

    // Add here any additional URL patterns specific to the corporate module if needed
    return 'HURO · Corporate portal';
  }

  private setDocumentTitle(pageTitle: string): void {
    this.title.setTitle(`${pageTitle}`);
  }
}
